import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, mapTo, first } from 'rxjs/operators';
import { HeatpumpService, AHheatPumpDAY, HeatPumpRTData } from 'ngx-core-services';

import {
  AppState,
  SystemsState,
  selectedSystemMetadata,
  requestSelectedHeatpumpCurrentData,
  getEmsConfig,
  updateSelectedHeatpumpCurrentData,
  setSelectedHeatpumpCurrentDataError,
  requestSelectedHeatpumpDailyTelemetryData,
  getSystemDefinitionConfiguration,
  updateSelectedHeatpumpDailyTelemetryData,
  requestSystemHeatpumpEnergyAggregation,
  updateSystemHeatpumpEnergyAggregation
} from '..';
@Injectable()
export class HeatpumpEffects {

  requestHeatpumpCurrentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSelectedHeatpumpCurrentData),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) =>
        from(this.getHeatpumpCurrentData(action.systemId, action.deviceId, action.forceRefresh, systemState)).pipe(
          map((heatpumpData: Partial<AHheatPumpDAY>) => updateSelectedHeatpumpCurrentData({ systemId: action.systemId, instanceId: action.instanceId, deviceId: action.deviceId, heatpumpData })),
          catchError(action => of(setSelectedHeatpumpCurrentDataError({ systemId: action.systemId, instanceId: action.instanceId, deviceId: action.deviceId, error: 'Failed to fetch heat pump data' })))
        )
      ),
    )
  );

  requestHeatpumpDailyTelemetry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSelectedHeatpumpDailyTelemetryData),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) =>
        from(this.getHeatpumpDailyTelemetryData(action.systemId, action.deviceId, action.date, action.forceRefresh, systemState)).pipe(
          map((heatpumpData: Partial<HeatPumpRTData>[]) => updateSelectedHeatpumpDailyTelemetryData({ systemId: action.systemId, instanceId: action.instanceId, deviceId: action.deviceId, heatpumpData })),
          catchError(action => of(setSelectedHeatpumpCurrentDataError({ systemId: action.systemId, instanceId: action.instanceId, deviceId: action.deviceId, error: 'Failed to fetch heat pump daily telemetry data' })))
        )
      ),
    )
  );

  requestHeatpumpEnergyAggregation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemHeatpumpEnergyAggregation),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) =>
        from(this.getHeatpumpEnergyAggregationData(action.systemId, action.deviceId, action.period, action.date, action.forceRefresh, systemState)).pipe(
          map((aggregationData: Partial<AHheatPumpDAY>[]) => updateSystemHeatpumpEnergyAggregation({ systemId: action.systemId, instanceId: action.instanceId, deviceId: action.deviceId, period: action.period, aggregationData })),
          catchError(action => of(setSelectedHeatpumpCurrentDataError({ systemId: action.systemId, instanceId: action.instanceId, deviceId: action.deviceId, error: 'Failed to fetch heat pump energy aggregation data' })))
        )
      ),
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private readonly heatpumpService: HeatpumpService,
  ) {}

  async getHeatpumpCurrentData(systemId: string ,deviceId: string, forceRefresh: boolean, state: SystemsState): Promise<Partial<AHheatPumpDAY>> {
    const system = state.systemList[systemId];
    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const emsConfig = getEmsConfig(system);
    const currentData = await this.heatpumpService.getCurrentHeatPumpData(deviceId, forceRefresh, emsConfig?.loggerId);
    return currentData;
  }

  async getHeatpumpDailyTelemetryData(systemId: string ,deviceId: string, date: string, forceRefresh: boolean, state: SystemsState): Promise<Partial<HeatPumpRTData>[]> {
    const system = state.systemList[systemId];
    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }
    const emsConfig = getEmsConfig(system);
    const systemConfiguration = getSystemDefinitionConfiguration(system);
    const telemetryData = await this.heatpumpService.getHeatPumpDataByRange(
      deviceId,
      date,
      `${systemConfiguration.tz}`,
      forceRefresh,
      emsConfig?.loggerId
    );
    return telemetryData;
  }

  async getHeatpumpEnergyAggregationData(systemId: string, deviceId: string, period: 'month' | 'year', date: Date, forceRefresh: boolean, state: SystemsState): Promise<Partial<AHheatPumpDAY>[]> {
    const system = state.systemList[systemId];
    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }
    const emsConfig = getEmsConfig(system);

    return period === 'month'
      ? await this.heatpumpService.getHeatPumpMonthlyAggregation(
          deviceId,
          date,
          forceRefresh,
          emsConfig?.loggerId
        )
      : await this.heatpumpService.getHeatPumpYearlyAggregation(
          deviceId,
          date,
          forceRefresh,
          emsConfig?.loggerId
        );
  }
}

